/**
 * MeasurementsDialog - Add/Edit measurement form component
 * Modal dialog for creating and updating measurements
 */

import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTranslation } from "@/hooks/useTranslation";
import type {
  Meranie,
  MeasurementFormData,
  TdeeTargetsFormData,
} from "../types";

type TargetField = keyof TdeeTargetsFormData;
type MacroField = "proteinG" | "fatG" | "carbsG";
type MacroEditMode = "keepEnergy" | "recalculateEnergy";
type TargetInputState = Record<Exclude<TargetField, "manuallyEdited">, string>;

const TARGET_FIELDS: Array<Exclude<TargetField, "manuallyEdited">> = [
  "tdee",
  "targetKcal",
  "deficitPercent",
  "proteinG",
  "fatG",
  "carbsG",
];

const MACRO_FIELDS: MacroField[] = ["proteinG", "fatG", "carbsG"];
const MACRO_KCAL: Record<MacroField, number> = {
  proteinG: 4,
  fatG: 9,
  carbsG: 4,
};

function parseTargets(
  meranie: Meranie | null
): Partial<TdeeTargetsFormData> | null {
  if (!meranie?.cieloveHodnoty) return null;

  try {
    return typeof meranie.cieloveHodnoty === "string"
      ? JSON.parse(meranie.cieloveHodnoty)
      : meranie.cieloveHodnoty;
  } catch {
    return null;
  }
}

function numberOrUndefined(
  value: FormDataEntryValue | null
): number | undefined {
  if (typeof value !== "string" || value.trim() === "") return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function targetDefault(
  targets: Partial<TdeeTargetsFormData> | null,
  key: Exclude<TargetField, "manuallyEdited">
): string {
  const value = targets?.[key];
  return typeof value === "number" || typeof value === "string"
    ? String(value)
    : "";
}

function buildInitialTargetState(
  targets: Partial<TdeeTargetsFormData> | null
): TargetInputState {
  return TARGET_FIELDS.reduce((acc, field) => {
    acc[field] = targetDefault(targets, field);
    return acc;
  }, {} as TargetInputState);
}

function toFiniteNumber(value: string): number | null {
  if (value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatTargetNumber(value: number): string {
  if (!Number.isFinite(value)) return "";
  return String(Math.max(0, Math.round(value)));
}

function macroEnergy(targets: Pick<TargetInputState, MacroField>): number {
  return MACRO_FIELDS.reduce((sum, field) => {
    const grams = toFiniteNumber(targets[field]) ?? 0;
    return sum + grams * MACRO_KCAL[field];
  }, 0);
}

function deficitPercent(tdee: string, targetKcal: string): string {
  const tdeeValue = toFiniteNumber(tdee);
  const targetValue = toFiniteNumber(targetKcal);
  if (!tdeeValue || tdeeValue <= 0 || targetValue === null) return "";
  return String(Math.round(((tdeeValue - targetValue) / tdeeValue) * 100));
}

function redistributeMacros(
  targets: TargetInputState,
  changedField: MacroField,
  oldValue: string,
  newValue: string
): TargetInputState {
  const oldGrams = toFiniteNumber(oldValue) ?? 0;
  const newGrams = toFiniteNumber(newValue) ?? 0;
  const deltaKcal = (newGrams - oldGrams) * MACRO_KCAL[changedField];

  if (deltaKcal === 0) return targets;

  const otherFields = MACRO_FIELDS.filter(field => field !== changedField);
  const otherEnergy = otherFields.reduce((sum, field) => {
    return sum + (toFiniteNumber(targets[field]) ?? 0) * MACRO_KCAL[field];
  }, 0);

  const fallbackShare = 1 / otherFields.length;
  const next = { ...targets };

  for (const field of otherFields) {
    const currentGrams = toFiniteNumber(targets[field]) ?? 0;
    const currentEnergy = currentGrams * MACRO_KCAL[field];
    const share = otherEnergy > 0 ? currentEnergy / otherEnergy : fallbackShare;
    const adjustedEnergy = Math.max(0, currentEnergy - deltaKcal * share);
    next[field] = formatTargetNumber(adjustedEnergy / MACRO_KCAL[field]);
  }

  return next;
}

function buildManualTargets(
  formData: FormData
): TdeeTargetsFormData | undefined {
  if (formData.get("tdeeTargetsTouched") !== "true") return undefined;

  const manualTargets: TdeeTargetsFormData = {
    tdee: numberOrUndefined(formData.get("tdee")),
    targetKcal: numberOrUndefined(formData.get("targetKcal")),
    deficitPercent: numberOrUndefined(formData.get("deficitPercent")),
    proteinG: numberOrUndefined(formData.get("proteinG")),
    fatG: numberOrUndefined(formData.get("fatG")),
    carbsG: numberOrUndefined(formData.get("carbsG")),
    manuallyEdited: true,
  };

  const hasAnyValue = Object.values(manualTargets).some(
    value => value !== undefined
  );
  return hasAnyValue ? manualTargets : undefined;
}

/** Render editable TDEE targets in the edit dialog. */
function TdeeTargetsBlock({ meranie }: { meranie: Meranie | null }) {
  const [mode, setMode] = useState<MacroEditMode>("keepEnergy");
  const [touched, setTouched] = useState(false);
  const parsedTargets = useMemo(() => parseTargets(meranie), [meranie]);
  const [targets, setTargets] = useState<TargetInputState>(() =>
    buildInitialTargetState(parsedTargets)
  );
  const { t } = useTranslation();

  useEffect(() => {
    setTargets(buildInitialTargetState(parsedTargets));
    setTouched(false);
    setMode("keepEnergy");
  }, [parsedTargets]);

  if (!meranie) return null;

  const markTouched = () => setTouched(true);

  const updateDeficitForEnergy = (
    next: TargetInputState
  ): TargetInputState => ({
    ...next,
    deficitPercent: deficitPercent(next.tdee, next.targetKcal),
  });

  const handlePlainChange = (
    field: Exclude<TargetField, "manuallyEdited">,
    value: string
  ) => {
    markTouched();
    setTargets(current => {
      const next = { ...current, [field]: value };
      return field === "tdee" || field === "targetKcal"
        ? updateDeficitForEnergy(next)
        : next;
    });
  };

  const handleMacroChange = (field: MacroField, value: string) => {
    markTouched();
    setTargets(current => {
      const normalizedValue = value;
      let next: TargetInputState = { ...current, [field]: normalizedValue };

      if (mode === "keepEnergy") {
        next = redistributeMacros(
          current,
          field,
          current[field],
          normalizedValue
        );
        next[field] = normalizedValue;
        return next;
      }

      next.targetKcal = formatTargetNumber(macroEnergy(next));
      return updateDeficitForEnergy(next);
    });
  };

  return (
    <div className="col-span-2 pt-3 border-t space-y-3">
      <input
        type="hidden"
        name="tdeeTargetsTouched"
        value={touched ? "true" : "false"}
      />
      <div className="space-y-1">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          {t("measurements:tdee.targets")}
        </p>
        <p className="text-xs text-muted-foreground">
          {t("measurements:tdee.description")}
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="macroEditMode">
          {t("measurements:tdee.macroEditMode")}
        </Label>
        <Select
          value={mode}
          onValueChange={value => setMode(value as MacroEditMode)}
        >
          <SelectTrigger id="macroEditMode">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="keepEnergy">
              Držať cieľové kcal a dorovnať ostatné makronutrienty
            </SelectItem>
            <SelectItem value="recalculateEnergy">
              Prepočítať cieľové kcal podľa makronutrientov
            </SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          V prvom režime sa pri zmene jednej živiny ostatné dve automaticky
          upravia tak, aby cieľové kcal zostali rovnaké. V druhom režime zostanú
          zadané živiny zachované a cieľové kcal sa prepočítajú podľa 4/9/4 kcal
          na gram.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor="tdee">TDEE (kcal)</Label>
          <Input
            id="tdee"
            name="tdee"
            type="number"
            step="1"
            min="0"
            value={targets.tdee}
            onChange={event => handlePlainChange("tdee", event.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="targetKcal">Cieľové kcal</Label>
          <Input
            id="targetKcal"
            name="targetKcal"
            type="number"
            step="1"
            min="0"
            value={targets.targetKcal}
            onChange={event =>
              handlePlainChange("targetKcal", event.target.value)
            }
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="deficitPercent">Deficit / nadbytok (%)</Label>
          <Input
            id="deficitPercent"
            name="deficitPercent"
            type="number"
            step="1"
            value={targets.deficitPercent}
            onChange={event =>
              handlePlainChange("deficitPercent", event.target.value)
            }
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="proteinG">Proteín (g)</Label>
          <Input
            id="proteinG"
            name="proteinG"
            type="number"
            step="1"
            min="0"
            value={targets.proteinG}
            onChange={event =>
              handleMacroChange("proteinG", event.target.value)
            }
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="fatG">Tuky (g)</Label>
          <Input
            id="fatG"
            name="fatG"
            type="number"
            step="1"
            min="0"
            value={targets.fatG}
            onChange={event => handleMacroChange("fatG", event.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="carbsG">Sacharidy (g)</Label>
          <Input
            id="carbsG"
            name="carbsG"
            type="number"
            step="1"
            min="0"
            value={targets.carbsG}
            onChange={event => handleMacroChange("carbsG", event.target.value)}
          />
        </div>
      </div>
    </div>
  );
}

interface MeasurementsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingMeranie: Meranie | null;
  onSubmit: (data: MeasurementFormData) => Promise<boolean>;
  selectedKlientId: number | null;
}

export function MeasurementsDialog({
  open,
  onOpenChange,
  editingMeranie,
  onSubmit,
  selectedKlientId,
}: MeasurementsDialogProps) {
  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedKlientId) return;

    const formData = new FormData(e.currentTarget);
    const vahaKg = formData.get("vahaKg") as string;
    const tukPercent = formData.get("tukPercent") as string;
    const svalovinaPercent = formData.get("svalovinaPercent") as string;
    const bazalnyMetabolizmus = formData.get("bazalnyMetabolizmus") as string;
    const visceralnytuk = formData.get("visceralnytuk") as string;

    const data: MeasurementFormData = {
      klientId: selectedKlientId,
      datum: formData.get("datum") as string,
      vahaKg: vahaKg || undefined,
      tukPercent: tukPercent || undefined,
      svalovinaPercent: svalovinaPercent || undefined,
      bazalnyMetabolizmus: bazalnyMetabolizmus || undefined,
      visceralnytuk: visceralnytuk ? Number(visceralnytuk) : undefined,
      cieloveHodnoty: editingMeranie ? buildManualTargets(formData) : undefined,
    };

    const success = await onSubmit(data);
    if (success) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingMeranie
              ? t("measurements:dialog.editMeasurement")
              : t("measurements:dialog.addNewMeasurement")}
          </DialogTitle>
          <DialogDescription>
            {t("measurements:dialog.enterBodyComposition")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-4 py-4">
            {/* Date */}
            <div className="space-y-2 col-span-2">
              <Label htmlFor="datum">
                {t("measurements:dialog.dateRequired")}
              </Label>
              <Input
                id="datum"
                name="datum"
                type="date"
                defaultValue={
                  editingMeranie?.datum ||
                  new Date().toISOString().split("T")[0]
                }
                required
              />
            </div>

            {/* Weight */}
            <div className="space-y-2">
              <Label htmlFor="vahaKg">
                {t("measurements:dialog.weightKg")}
              </Label>
              <Input
                id="vahaKg"
                name="vahaKg"
                type="number"
                step="0.1"
                defaultValue={editingMeranie?.vahaKg || ""}
              />
            </div>

            {/* Body Fat */}
            <div className="space-y-2">
              <Label htmlFor="tukPercent">
                {t("measurements:dialog.bodyFat")}
              </Label>
              <Input
                id="tukPercent"
                name="tukPercent"
                type="number"
                step="0.1"
                defaultValue={editingMeranie?.tukPercent || ""}
              />
            </div>

            {/* Muscle Mass */}
            <div className="space-y-2">
              <Label htmlFor="svalovinaPercent">
                {t("measurements:dialog.muscleMass")}
              </Label>
              <Input
                id="svalovinaPercent"
                name="svalovinaPercent"
                type="number"
                step="0.1"
                defaultValue={editingMeranie?.svalovinaPercent || ""}
              />
            </div>

            {/* Visceral Fat */}
            <div className="space-y-2">
              <Label htmlFor="visceralnytuk">
                {t("measurements:dialog.visceralFat")}
              </Label>
              <Input
                id="visceralnytuk"
                name="visceralnytuk"
                type="number"
                step="0.1"
                defaultValue={editingMeranie?.visceralnytuk || ""}
              />
            </div>

            {/* Basal Metabolic Rate */}
            <div className="space-y-2">
              <Label htmlFor="bazalnyMetabolizmus">
                {t("measurements:dialog.basalMetabolicRate")}
              </Label>
              <Input
                id="bazalnyMetabolizmus"
                name="bazalnyMetabolizmus"
                type="number"
                step="1"
                defaultValue={editingMeranie?.bazalnyMetabolizmus || ""}
              />
            </div>

            {/* TDEE targets (editable in edit mode) */}
            <TdeeTargetsBlock meranie={editingMeranie} />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t("common:actions.cancel")}
            </Button>
            <Button type="submit">
              {editingMeranie
                ? t("common:actions.update")
                : t("common:actions.create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
